import * as React from 'react';
import './StudentTable.css';
import { Avatar, Box, Typography, Popover } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import ReactCountryFlag from 'react-country-flag';
import { getCodeList } from 'country-list';
import LearnerTableSkeleton from '../../../../SkeletonEffect/LearnerTableSkeleton/LearnerTableSkeleton';
import { getAccountStatus } from '../../../../../utils/auditActorUtils';

const LevelToTeachColor = {
  university: {
    color: '#14B334',
    bgcolor: '#F2FFF5',
    value: 'UN',
    value2: 'University',
  },
  high_school: {
    color: '#E0AD14',
    bgcolor: '#FFFBF0',
    value: 'HS',
    value2: 'High School',
  },
  middle_school: {
    color: '#6B60E7',
    bgcolor: '#F0EFFF',
    value: 'MS',
    value2: 'Middle School',
  },
  elementary: {
    color: '#E0AD14',
    bgcolor: '#FFFBF0',
    value: 'EL',
    value2: 'Elementary',
  },
};

const StatusColor = {
  enable: { color: '#14B334', bgcolor: '#F2FFF5', value: 'Enable' },
  disable: { color: '#949DA6', bgcolor: '#F5F5F5', value: 'Disable' },
};

const FilterCategoryValue = {
  Elementary: { value: 'elementary' },
  University: { value: 'university' },
  'High School': { value: 'high_school' },
  'Middle School': { value: 'middle_school' },
  elementary: { value: 'elementary' },
  university: { value: 'university' },
  high_school: { value: 'high_school' },
  middle_school: { value: 'middle_school' },
};

const countryListObject = getCodeList();
const CountriesList = Object.entries(countryListObject).map(([code, name]) => ({
  code,
  name,
}));

// Country name mapping for common variations
const countryNameMapping = {
  iran: 'iran, islamic republic of',
  russia: 'russian federation',
  'south korea': 'korea, republic of',
  'north korea': "korea, democratic people's republic of",
  usa: 'united states',
  'united states': 'united states',
  uk: 'united kingdom',
  britain: 'united kingdom',
  england: 'united kingdom',
  uae: 'united arab emirates',
  emirates: 'united arab emirates',
  vietnam: 'viet nam',
  bolivia: 'bolivia, plurinational state of',
  venezuela: 'venezuela, bolivarian republic of',
  tanzania: 'tanzania, united republic of',
  macedonia: 'macedonia, the former yugoslav republic of',
  moldova: 'moldova, republic of',
  syria: 'syrian arab republic',
  laos: "lao people's democratic republic",
  brunei: 'brunei darussalam',
  congo: 'congo, the democratic republic of the',
  'ivory coast': "côte d'ivoire",
  'czech republic': 'czechia',
  'east timor': 'timor-leste',
  swaziland: 'eswatini',
  myanmar: 'myanmar',
  burma: 'myanmar',
};

export default function StudentTable({
  searchTerm,
  studentData,
  category,
  country,
  value,
  // selectionMode = false,
  // selectedIds = new Set(),
  // onSelectOne = () => {},
  // onSelectAll = () => {},
}) {
  // Server-side sorting: use data order from parent
  const serverOrderedRows = Array.isArray(studentData) ? studentData : [];

  const navigate = useNavigate();

  const handleCellClick = (event, id) => {
    // Ignore clicks originating from a checkbox
    if (event.target.closest('input[type="checkbox"]')) return;
    // Check if the click is from the actions column
    const isActionsCell = event.target.closest('td:last-child');
    if (!isActionsCell) {
      navigate(`/admin/users/learner-details/${id}`);
    }
  };

  console.log(serverOrderedRows);

  const PopoverSchoolLevel = ({ level, rowId }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handlePopoverOpen = event => {
      setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
      setAnchorEl(null);
    };

    return (
      <div
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
        className="flex items-center cursor-pointer w-full"
      >
        <Box className="flex items-center gap-2">
          <Box
            style={{
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '4px 8px',
              borderRadius: '20px',
              backgroundColor: LevelToTeachColor[level]?.bgcolor,
              color: LevelToTeachColor[level]?.color,
              fontFamily: 'Poppins !important',
              fontSize: '14px !important',
              fontStyle: 'normal !important',
              fontWeight: '400 !important',
              lineHeight: 'normal !important',
            }}
          >
            <Typography className="level-text">
              {LevelToTeachColor[level]?.value || '---'}
            </Typography>
          </Box>
        </Box>

        {level !== null && (
          <Popover
            id={`level-popover-${rowId}`}
            sx={{
              pointerEvents: 'none',
              '& .MuiPopover-paper': {
                borderRadius: '8px',
                boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.1)',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '-6px',
                  left: '20px',
                  width: '12px',
                  height: '12px',
                  backgroundColor: 'white',
                  transform: 'rotate(45deg)',
                  borderLeft: '1px solid rgba(0, 0, 0, 0.08)',
                  borderTop: '1px solid rgba(0, 0, 0, 0.08)',
                },
              },
            }}
            open={open}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            onClose={handlePopoverClose}
            disableRestoreFocus
            PaperProps={{
              onMouseEnter: handlePopoverOpen,
              onMouseLeave: handlePopoverClose,
              sx: {
                pointerEvents: 'auto',
                minWidth: '150px',
              },
            }}
          >
            <Box
              sx={{
                p: 1,
                '& > :not(:last-child)': {
                  borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                },
              }}
            >
              <Box
                style={{
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '4px 8px',
                  borderRadius: '20px',
                  backgroundColor: LevelToTeachColor[level]?.bgcolor,
                  color: LevelToTeachColor[level]?.color,
                  fontFamily: 'Poppins !important',
                  fontSize: '12px !important',
                  fontStyle: 'normal !important',
                  fontWeight: '400 !important',
                  marginBottom: '2px',
                }}
              >
                <Typography className="level-text">{LevelToTeachColor[level]?.value2}</Typography>
              </Box>
            </Box>
          </Popover>
        )}
      </div>
    );
  };

  const renderCountry = countryName => {
    if (!countryName) return '---';

    // First try exact match
    let countryMatch = CountriesList.find(
      country => country.name.toLowerCase() === countryName.toLowerCase()
    );

    // If no exact match, try with mapping
    if (!countryMatch) {
      const mappedName = countryNameMapping[countryName.toLowerCase()];
      if (mappedName) {
        countryMatch = CountriesList.find(
          country => country.name.toLowerCase() === mappedName.toLowerCase()
        );
      }
    }

    return countryMatch ? (
      <Box className="flex items-center gap-4">
        <Typography>{countryName}</Typography>
        <ReactCountryFlag countryCode={countryMatch.code} svg />
      </Box>
    ) : (
      <Typography>{countryName}</Typography>
    );
  };

  return (
    <Box className="student-table-container">
      {serverOrderedRows.length === 0 ? (
        <table className="student-table">
          <thead className="student-table-header">
            <tr>
              <th className="student-th">Name</th>
              <th className="student-th">School Level</th>
              <th className="student-th">Total Session</th>
              <th className="student-th">Total Spent</th>
              <th className="student-th">Country</th>
              <th className="student-th">Enrolled on</th>
              <th className="student-th">Status</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan={7} className="student-empty-cell">
                <Box className="student-empty-state">
                  <Typography className="student-empty-text">No learners available</Typography>
                </Box>
              </td>
            </tr>
          </tbody>
        </table>
      ) : (
        <table className="student-table">
          <thead className="student-table-header">
            <tr>
              <th className="student-th">Name</th>
              <th className="student-th">School Level</th>
              <th className="student-th">Total Session</th>
              <th className="student-th">Total Spent</th>
              <th className="student-th">Country</th>
              <th className="student-th">Enrolled on</th>
              <th className="student-th">Status</th>
            </tr>
          </thead>
          <tbody className="student-table-body">
            {serverOrderedRows.map(row => {
              // Treat an account as enabled only when it is active AND no feature
              // has been individually blocked; any block shows as "Disable".
              const isEnabled = getAccountStatus(row) === 'enabled';
              return (
                <tr
                  key={row.id}
                  className={!isEnabled ? 'student-table-row-disabled' : 'student-table-row'}
                  onClick={e => handleCellClick(e, row.id)}
                >
                  <td className="student-td">
                    <Box className="student-name-container">
                      <Avatar
                        src={row.profile_image || '/broken-image.jpg'}
                        className="student-avatar"
                      />
                      <Typography className="student-name-text">
                        {row.first_name} {row.last_name}
                      </Typography>
                    </Box>
                  </td>
                  <td className="student-td">
                    {row.school_level !== null ? (
                      <PopoverSchoolLevel level={row.school_level} rowId={row.id} />
                    ) : (
                      <Typography className="level-text">---</Typography>
                    )}
                  </td>
                  <td className="student-td">{row?.total_sessions ? row?.total_sessions : '0'}</td>
                  <td className="student-td">{row?.total_spent ? row?.total_spent : '0'}</td>
                  <td className="student-td">{renderCountry(row?.country)}</td>
                  <td className="student-td">
                    {row?.updated_at ? dayjs(row?.updated_at).format('DD-MM-YYYY') : '---'}
                  </td>
                  <td className="student-td">
                    <Box
                      className="student-status-container"
                      style={{
                        border: `1px solid ${
                          isEnabled ? StatusColor['enable']?.color : StatusColor['disable']?.color
                        }`,
                        backgroundColor: isEnabled
                          ? StatusColor['enable']?.bgcolor
                          : StatusColor['disable']?.bgcolor,
                        color: isEnabled
                          ? StatusColor['enable']?.color
                          : StatusColor['disable']?.color,
                      }}
                    >
                      <Typography
                        className="student-status-dot"
                        style={{
                          backgroundColor: isEnabled
                            ? StatusColor['enable']?.color
                            : StatusColor['disable']?.color,
                        }}
                      ></Typography>
                      <Typography className="status-text">
                        {isEnabled ? 'Enable' : 'Disable'}
                      </Typography>
                    </Box>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Box>
  );
}
